import React, {useState, useRef, useEffect, useCallback} from 'react';
import {flushSync} from 'react-dom';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components';
import {Modal, Icon, FlexRow} from '@forecast-it/design-system';
import {fetchQuery, graphql} from 'relay-runtime';
import environment from '../../../RelayEnvironment';
import Util from '../../../forecast-app/shared/util/util';
import CreateApiKeyMutation from '../../../mutations/create_api_key_mutation';
import type {AiProjectPlanningModal_ApiKey_Query$data} from './__generated__/AiProjectPlanningModal_ApiKey_Query.graphql';
import type {
	AiProjectPlanningModalProps,
	AttachedFile,
	ChatMessage,
	Phase,
	PlaceholderProgress,
	ProjectProposal,
	StreamCallbacks,
	Warning,
	ReviewRequestedPayload,
	ReconciliationQuestion,
	SowExtractionStep,
} from './types';
import {SimpleUUID, SOW_CHECKLIST_STEPS, isReconciliationQuestionAlreadyFilled, deriveNovaFilledFields} from './constants';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import {invokeAgent, resumeAgent} from './agentApi';
import {NOVA_AVATAR_GRADIENT, NOVA_LOADING_COLOR} from './colors';
import {Body, Divider, SowTitleBadge} from './styles';
import ChatPanel from './ChatPanel';
import ProposalPanel from './ProposalPanel';
import SowExtractionProgress from './SowExtractionProgress';
import SowReviewFormPanel from './SowReviewFormPanel';

const apiKeyQuery = graphql`
	query AiProjectPlanningModal_ApiKey_Query($includeSowData: Boolean!) {
		viewer {
			backendId
			fullName
			company {
				id
				name
				apiKeys(first: 1000) {
					edges {
						node {
							key
							active
							createdBy {
								id
							}
						}
					}
				}
				clients(first: 1000) @include(if: $includeSowData) {
					edges {
						node {
							id
							name
						}
					}
				}
				allPersons(first: 500, onlyActive: true) @include(if: $includeSowData) {
					edges {
						node {
							id
							fullName
						}
					}
				}
			}
		}
	}
`;

const TitleIconBadge = Styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 8px;
	background: ${NOVA_AVATAR_GRADIENT};
	flex-shrink: 0;
`;

// Override Modal.Content to support the side-by-side panel layout with internal scroll areas
const PanelContent = Styled(Modal.Content)`
	overflow: hidden;
	flex: 1;
	min-height: 0;
	padding: 0;
`;

// ---------------------------------------------------------------------------
// Session persistence
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'forecast_ai_planning_session';

interface SessionData {
	threadId: string;
	proposal: ProjectProposal | null;
	phase: Phase;
	messages: ChatMessage[];
	mode?: 'sow' | 'chat'; // prevents SOW sessions from loading in legacy chat mode
	sowScreen?: 'extracting' | 'review';
	novaFilledFields?: string[];
	sowFilename?: string | null;
	// toolStatuses intentionally omitted — ephemeral tool-call indicators reset on mount
}

function loadSession(): SessionData | null {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

function saveSession(data: SessionData) {
	try {
		const safeMessages = data.messages.map(m => (m.isStreaming ? {...m, isStreaming: false} : m));
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify({...data, messages: safeMessages}));
	} catch {
		// storage full or unavailable — non-fatal
	}
}

function clearSession() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		// ignore
	}
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

const EMPTY_PROPOSAL: ProjectProposal = {
	name: '',
	start_date: null,
	end_date: null,
	budget_type: 'TIME_AND_MATERIALS',
	budget: null,
	color: NOVA_LOADING_COLOR,
	team_members: [],
	deliverables: [],
	client_id: null,
	client_name: null,
	owner_name: null,
	owner_person_id: null,
};

function mergeProposalEdits(
	incoming: ProjectProposal,
	prev: ProjectProposal,
	edited: Set<string>,
	viewerPersonId: number | null,
	viewerFullName: string | null
): ProjectProposal {
	return {
		...incoming,
		name: edited.has('name') ? prev.name : incoming.name ?? prev.name,
		budget_type: edited.has('budget_type') ? prev.budget_type : incoming.budget_type ?? prev.budget_type,
		start_date: edited.has('start_date') ? prev.start_date : incoming.start_date || prev.start_date,
		end_date: edited.has('end_date') ? prev.end_date : incoming.end_date || prev.end_date,
		budget: edited.has('budget') ? prev.budget : incoming.budget ?? prev.budget,
		client_id: edited.has('client_id') ? prev.client_id : incoming.client_id ?? prev.client_id,
		client_name: edited.has('client_name') ? prev.client_name : incoming.client_name ?? prev.client_name,
		owner_person_id: edited.has('owner_person_id') ? prev.owner_person_id : prev.owner_person_id ?? viewerPersonId ?? null,
		owner_name: edited.has('owner_person_id')
			? prev.owner_name
			: incoming.owner_name ?? prev.owner_name ?? viewerFullName ?? null,
	};
}

const AiProjectPlanningModal: React.FC<AiProjectPlanningModalProps> = ({
	closeModal,
	personId,
	initialFile,
	onSwitchToManualCreate,
}) => {
	const {formatMessage} = useIntl();
	const history = useHistory();
	// Computed once at mount via lazy initializer. Discarded when:
	//  • initialFile provided (fresh SOW upload always starts clean)
	//  • saved session is from a SOW run but we're opening in legacy chat mode
	const [session] = useState<SessionData | null>(() => {
		if (initialFile) return null;
		const s = loadSession();
		return s?.mode === 'sow' ? null : s;
	});

	const [phase, setPhase] = useState<Phase>(session?.phase ?? 'idle');
	const [messages, setMessages] = useState<ChatMessage[]>(session?.messages ?? []);
	const [proposal, setProposal] = useState<ProjectProposal | null>(session?.proposal ?? null);
	const [warnings, setWarnings] = useState<Warning[]>([]);
	const [toolStatuses, setToolStatuses] = useState<Record<string, boolean>>({});
	const [resolvedApiKey, setResolvedApiKey] = useState('');
	// Tenant identity for telemetry headers — sourced from the same viewer.company
	// Relay query that resolves the API key. Stays undefined when the query is in
	// flight or returns no company; the agent calls then omit the headers entirely.
	const [companyId, setCompanyId] = useState<string | undefined>(undefined);
	const [companyName, setCompanyName] = useState<string | undefined>(undefined);
	const [sowScreen, setSowScreen] = useState<'extracting' | 'review'>(session?.sowScreen ?? 'extracting');
	const [extractionSteps, setExtractionSteps] = useState<SowExtractionStep[]>(
		SOW_CHECKLIST_STEPS.map(label => ({label, status: 'pending' as const}))
	);
	const [novaFilledFields, setNovaFilledFields] = useState<string[]>(() => {
		if (session?.novaFilledFields?.length) return session.novaFilledFields;
		// Session was saved before backend sends nova_filled_fields — derive from proposal.
		const p = session?.proposal;
		if (!p) return [];
		return deriveNovaFilledFields(p);
	});
	const [, setRequiredFieldsStatus] = useState<Record<string, boolean> | null>(null);
	const [createPlaceholdersEnabled, setCreatePlaceholdersEnabled] = useState(false);
	const [sowFilename, setSowFilename] = useState<string | null>(session?.sowFilename ?? null);
	const [clients, setClients] = useState<{id: string; name: string}[]>([]);
	const [persons, setPersons] = useState<{id: string; backendId: number; fullName: string}[]>([]);
	const [placeholderProgresses, setPlaceholderProgresses] = useState<PlaceholderProgress[]>([]);
	// Viewer identity — populated after the API key query resolves. Used to pre-fill project owner.
	const viewerPersonIdRef = useRef<number | null>(null);
	const viewerFullNameRef = useRef<string | null>(null);
	// Tracks the file currently being processed — updated on Replace so the FileCard stays accurate
	const [activeFile, setActiveFile] = useState<AttachedFile | undefined>(initialFile);
	const hasAutoStarted = useRef(false);
	// Tracks whether reconciliation is still in progress (RECONCILIATION_QUESTION fired but
	// REVIEW_REQUESTED has not yet fired). Used to prevent onFinished from incorrectly
	// setting phase='awaiting_approval' when a preliminary proposal is already in state
	// (Fix 2 emits a preliminary proposal during reconciliation, which sets proposalRef
	// before reconciliation completes — without this flag, isReconciliation resolves to
	// false and resume sends refinement instead of answer).
	const reconciliationActiveRef = useRef(false);
	// Synced synchronously in onProposal so it is always current when onReconciliationQuestion
	// fires in the same SSE flush (proposalRef is updated via useEffect, which is too late).
	const unfilledRolesRef = useRef<string[]>([]);
	// Tracks whether the user has manually toggled the "create placeholders" checkbox.
	// Prevents subsequent REVIEW_REQUESTED events from resetting the user's choice.
	const createPlaceholdersEditedRef = useRef(false);
	// Tracks which proposal fields the user has manually edited in the form.
	// When a new proposal arrives from the bot, only user-edited fields are preserved;
	// un-touched fields accept the bot's updated value (e.g. dates set via chat).
	const userEditedFieldsRef = useRef<Set<string>>(new Set());
	// Stores a reconciliation answer to auto-send after the current stream ends (phase → idle).
	// Used to silently skip questions about fields the user already filled manually.
	const pendingAutoAnswerRef = useRef<string | null>(null);

	// Resolve the current user's active API key. Provision one if none exists.
	// Matches the chat-agent behavior in PrimaryNavigationRight — fetch all keys,
	// filter to the active one created by the viewer, and create on miss.
	useEffect(() => {
		let cancelled = false;
		fetchQuery(environment.getInstance(), apiKeyQuery, {includeSowData: Boolean(initialFile)})
			.toPromise()
			.then(rawData => {
				if (cancelled) return;
				const data = rawData as AiProjectPlanningModal_ApiKey_Query$data | undefined;
				const viewer = data?.viewer;
				const company = viewer?.company;
				if (company?.id) setCompanyId(company.id);
				if (company?.name) setCompanyName(company.name);
				// Capture viewer identity for project owner pre-fill.
				if (viewer?.backendId != null) viewerPersonIdRef.current = viewer.backendId;
				if (viewer?.fullName) viewerFullNameRef.current = viewer.fullName;
				// Pre-fill owner immediately if the proposal exists but has no owner set (session restore path).
				// For fresh sessions onReviewRequested handles first arrival; this catches the restore case.
				// Guard only on backendId — name may be null but the ID alone is enough to assign the owner.
				if (viewer?.backendId != null) {
					setProposal(prev =>
						prev && prev.owner_person_id == null
							? {...prev, owner_person_id: viewer.backendId, owner_name: viewer.fullName ?? null}
							: prev
					);
				}
				// All setState calls below are guarded by the `if (cancelled) return` above.
				const clientEdges = company?.clients?.edges ?? [];
				setClients(clientEdges.flatMap(e => (e?.node ? [{id: e.node.id, name: e.node.name}] : [])));
				const personEdges = company?.allPersons?.edges ?? [];
				setPersons(
					personEdges.flatMap(e => {
						if (!e?.node?.id || !e.node.fullName) return [];
						const backendId = Util.getIdFromBase64String(e.node.id);
						return !isNaN(backendId) ? [{id: e.node.id, backendId, fullName: e.node.fullName}] : [];
					})
				);
				const edges = company?.apiKeys?.edges ?? [];
				const mine = edges.find(e => {
					const node = e?.node;
					if (!node?.active || !node?.createdBy?.id || !node?.key) return false;
					return Util.getIdFromBase64String(node.createdBy.id) === personId;
				});
				if (mine?.node?.key) {
					setResolvedApiKey(mine.node.key);
					return;
				}
				const companyRelayId = company?.id;
				if (!companyRelayId) return;
				Util.CommitMutation(
					CreateApiKeyMutation,
					companyRelayId,
					(response: {createApiKey?: {apiKey?: {node?: {key?: string; active?: boolean}}}}) => {
						if (cancelled) return;
						const node = response?.createApiKey?.apiKey?.node;
						if (node?.active && node?.key) setResolvedApiKey(node.key);
					}
				);
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
		};
	}, [personId]);

	// map from toolCallId (from SSE) -> message id in chat
	const toolCallIdToMsgId = useRef<Record<string, string>>({});
	// map from tool name -> message id — collapses repeated calls into one row
	const toolNameToMsgId = useRef<Record<string, string>>({});
	// active (in-flight) call count per grouped message id
	const activeToolCalls = useRef<Record<string, number>>({});
	const threadIdRef = useRef<string>(session?.threadId ?? '');
	const proposalRef = useRef<ProjectProposal | null>(session?.proposal ?? null);
	const createdProjectIdRef = useRef<number | null>(null);
	const createdProjectRelayIdRef = useRef<string>('');
	const createdDeliverableCountRef = useRef<number>(0);
	const createdRoleCountRef = useRef<number>(0);
	const abortRef = useRef<AbortController | null>(null);
	const refiningChipIdRef = useRef<string | null>(null);
	const pendingAutoApproveRef = useRef(false);
	const handleApproveRef = useRef<() => void>(() => undefined);
	const autoAnswerDateQuestionRef = useRef<((answer: string) => void) | null>(null);
	// Set by onReconciliationQuestion when a date field is already filled.
	// Consumed by onFinished once the current stream ends cleanly.
	const pendingReconciliationAnswerRef = useRef<string | null>(null);
	const [isClassificationBlocked, setIsClassificationBlocked] = useState(false);
	// The rejection reason shown on the "thinking" page (SowExtractionProgress) when the
	// agent rejects the document, e.g. "This document appears to be a Master Service
	// Agreement...". Falls back to a generic message if the backend didn't send one.
	const [classificationBlockReason, setClassificationBlockReason] = useState<string | undefined>(undefined);
	// Set to true in onError so onFinished can distinguish a clean no-proposal end
	// (classification_blocked path) from a generic backend error.
	const hadErrorInStreamRef = useRef(false);
	// Guard against state updates after unmount (e.g. setTimeout callbacks).
	const isUnmountedRef = useRef(false);

	const isSowMode = Boolean(initialFile);

	// Abort any in-flight stream on unmount
	useEffect(() => {
		return () => {
			isUnmountedRef.current = true;
			abortRef.current?.abort();
		};
	}, []);

	// Persist session on meaningful state changes.
	// toolStatuses is intentionally excluded — it flips on every SSE tool event,
	// triggering JSON.stringify of the full messages array ~12–16 times per extraction.
	// Tool-call chip state is ephemeral and resets on mount anyway.
	useEffect(() => {
		if (threadIdRef.current || messages.length > 0) {
			saveSession({
				threadId: threadIdRef.current,
				proposal,
				phase,
				messages,
				mode: isSowMode ? 'sow' : 'chat',
				sowScreen,
				novaFilledFields,
				sowFilename,
			});
		}
	}, [messages, proposal, phase, isSowMode, sowScreen, novaFilledFields, sowFilename]);

	// Keep proposalRef in sync so the approve handler can read the latest proposal
	useEffect(() => {
		proposalRef.current = proposal;
	}, [proposal]);

	const addMessage = useCallback((msg: ChatMessage) => {
		setMessages(prev => [...prev, msg]);
	}, []);

	const updateMessage = useCallback((id: string, updater: (m: ChatMessage) => ChatMessage) => {
		setMessages(prev => prev.map(m => (m.id === id ? updater(m) : m)));
	}, []);

	const updateStepStatus = useCallback((stepName: string, status: SowExtractionStep['status']) => {
		setExtractionSteps(prev => prev.map(s => (s.label === stepName ? {...s, status} : s)));
	}, []);

	const buildCallbacks = useCallback((): StreamCallbacks => {
		hadErrorInStreamRef.current = false;
		return {
			onNewAssistantMessage: (msgId: string) => {
				addMessage({id: msgId, role: 'assistant', content: '', isStreaming: true});
			},
			onText: (msgId: string, delta: string) => {
				updateMessage(msgId, m => ({...m, content: m.content + delta}));
			},
			onMessageStreamEnd: (msgId: string) => {
				if (msgId) updateMessage(msgId, m => ({...m, isStreaming: false}));
			},
			onProposal: (p: ProjectProposal) => {
				if (p.unfilled_roles?.length) {
					unfilledRolesRef.current = p.unfilled_roles;
				}
				const prev = proposalRef.current;
				let merged: ProjectProposal;
				if (!prev) {
					userEditedFieldsRef.current.clear();
					merged = {
						...p,
						owner_person_id: viewerPersonIdRef.current ?? null,
						owner_name: viewerFullNameRef.current ?? null,
					};
				} else {
					merged = mergeProposalEdits(
						p,
						prev,
						userEditedFieldsRef.current,
						viewerPersonIdRef.current,
						viewerFullNameRef.current
					);
				}
				// Sync immediately so onReconciliationQuestion (same SSE flush) can read it.
				proposalRef.current = merged;
				setProposal(merged);
				setWarnings([]);
			},
			onWarnings: (w: Warning[]) => {
				setWarnings(w);
			},
			onRefiningStarted: () => {
				const refiningId = SimpleUUID();
				refiningChipIdRef.current = refiningId;
				setToolStatuses(prev => ({...prev, [refiningId]: false}));
				addMessage({id: refiningId, role: 'tool', content: '', toolName: '__refining__', isStreaming: true});
			},
			onCommitted: (projectId: number | null, relayProjectId?: string, deliverableCount?: number, roleCount?: number) => {
				createdProjectIdRef.current = projectId;
				createdProjectRelayIdRef.current = relayProjectId ?? '';
				createdDeliverableCountRef.current = deliverableCount ?? 0;
				createdRoleCountRef.current = roleCount ?? 0;
				setPhase('committed');
				setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
			},
			onPlaceholderProgress: placeholders => {
				setPlaceholderProgresses(placeholders);
			},
			onFinished: () => {
				setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
				if (refiningChipIdRef.current) {
					const chipId = refiningChipIdRef.current;
					refiningChipIdRef.current = null;
					setToolStatuses(prev => ({...prev, [chipId]: true}));
				}
				if (createdProjectIdRef.current) {
					const relayId = createdProjectRelayIdRef.current || `P${createdProjectIdRef.current}`;
					const projectName = proposalRef.current?.name ?? '';
					const dCount = createdDeliverableCountRef.current;
					const rCount = createdRoleCountRef.current;
					clearSession();
					closeModal();
					history.push(`/project/${encodeURIComponent(relayId)}/scoping`);
					const toastMsg =
						dCount > 0 || rCount > 0
							? formatMessage(
									{id: 'sow_review.success_toast'},
									{projectName, deliverables: dCount, roles: rCount}
							  )
							: formatMessage({id: 'sow_review.success_toast_simple'}, {projectName});
					createToast({duration: 5000, message: toastMsg});
					return;
				}

				// Consume any pending silent date answer queued by onReconciliationQuestion.
				// The stream has now ended cleanly — safe to start the next resumeAgent.
				const pendingAnswer = pendingReconciliationAnswerRef.current;
				if (pendingAnswer) {
					pendingReconciliationAnswerRef.current = null;
					// Also clear pendingAutoAnswerRef: if both were queued (two RECONCILIATION_QUESTION
					// events in one stream), the date answer takes priority. The 'Skip' was for the
					// same LangGraph run and would be stale once the date answer resumes the graph.
					pendingAutoAnswerRef.current = null;
					setTimeout(() => {
						if (!isUnmountedRef.current) autoAnswerDateQuestionRef.current?.(pendingAnswer);
					}, 0);
					return;
				}

				// proposalRef.current may be the preliminary proposal from reconcile_sow
				// Phase 1. Only move to awaiting_approval when reconciliation is fully
				// done (reconciliationActiveRef is false = REVIEW_REQUESTED has fired).
				setPhase(prev => {
					if (prev === 'error') return prev;
					return proposalRef.current && !reconciliationActiveRef.current ? 'awaiting_approval' : 'idle';
				});
				// Transition to review when a SOW stream ends with something to show —
				// covers hard-block paths (retainer/MSA/unsupported) where neither
				// REVIEW_REQUESTED nor RECONCILIATION_QUESTION fires. setState is a
				// no-op if already 'review'.
				if (initialFile) {
					// Classification block (retainer/MSA/non-English): stream ended cleanly with
					// no proposal and no generic error. hadErrorInStreamRef distinguishes this from
					// backend errors where isClassificationBlocked would be misleading.
					const isClassificationBlockEnding =
						!proposalRef.current && !reconciliationActiveRef.current && !hadErrorInStreamRef.current;
					if (isClassificationBlockEnding) {
						setIsClassificationBlocked(true);
						// Stay on the "thinking" page — SowExtractionProgress renders the
						// rejection message there (with a way back to manual setup) instead
						// of moving to a review screen with nothing to review.
					} else {
						setSowScreen('review');
					}
				}
			},
			onClassificationBlocked: (reason?: string) => {
				if (reason) {
					addMessage({id: SimpleUUID(), role: 'assistant', content: reason});
				}
				setClassificationBlockReason(reason);
				setIsClassificationBlocked(true);
			},
			onBudgetExhausted: (message: string) => {
				// Not a failure, so no `Error:` prefix and no error phase: the account
				// has simply used what it bought. Shown as an ordinary assistant
				// message, with the screen moved off the extraction checklist so it is
				// visible at all - a refusal arrives before any stream starts, so
				// nothing else would take the user off that spinner.
				setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
				addMessage({id: SimpleUUID(), role: 'assistant', content: message});
				// Back to rest, not 'error'. The run is over and nothing is pending, so
				// leaving it 'running' would keep the typing indicator and the Stop
				// button up and make the modal look like it is still working.
				setPhase('idle');
				if (initialFile) {
					setSowScreen('review');
				}
			},
			onError: (message: string) => {
				hadErrorInStreamRef.current = true;
				setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
				if (refiningChipIdRef.current) {
					const chipId = refiningChipIdRef.current;
					refiningChipIdRef.current = null;
					setToolStatuses(prev => ({...prev, [chipId]: true}));
				}
				addMessage({id: SimpleUUID(), role: 'assistant', content: `Error: ${message}`});
				setPhase('error');
				// Transition to review so the chat panel (which shows the error) is visible.
				// Unlike the classification-blocked path (which now renders its rejection
				// message directly on SowExtractionProgress), a generic backend error has no
				// dedicated display there — it only surfaces via the chat panel on this screen.
				if (initialFile) setSowScreen('review');
			},
			onToolCall: (toolCallId: string, toolName: string) => {
				const existingMsgId = toolNameToMsgId.current[toolName];
				if (existingMsgId) {
					toolCallIdToMsgId.current[toolCallId] = existingMsgId;
					activeToolCalls.current[existingMsgId] = (activeToolCalls.current[existingMsgId] ?? 1) + 1;
					setToolStatuses(prev => ({...prev, [existingMsgId]: false}));
					updateMessage(existingMsgId, m => ({
						...m,
						callCount: activeToolCalls.current[existingMsgId],
						isStreaming: true,
					}));
				} else {
					const msgId = SimpleUUID();
					toolCallIdToMsgId.current[toolCallId] = msgId;
					toolNameToMsgId.current[toolName] = msgId;
					activeToolCalls.current[msgId] = 1;
					setToolStatuses(prev => ({...prev, [msgId]: false}));
					addMessage({id: msgId, role: 'tool', content: '', toolName, callCount: 1, isStreaming: true});
				}
			},
			onToolResult: (toolCallId: string) => {
				const msgId = toolCallIdToMsgId.current[toolCallId];
				if (msgId) {
					const remaining = (activeToolCalls.current[msgId] ?? 1) - 1;
					activeToolCalls.current[msgId] = Math.max(0, remaining);
					if (remaining <= 0) {
						setToolStatuses(prev => ({...prev, [msgId]: true}));
						updateMessage(msgId, m => ({...m, isStreaming: false}));
					}
				}
			},
			onStepStarted: (stepName: string) => {
				// flushSync forces React to render immediately for each step update,
				// preventing batching that would make all ticks appear simultaneously.
				flushSync(() => updateStepStatus(stepName, 'active'));
			},
			onStepFinished: (stepName: string) => {
				flushSync(() => updateStepStatus(stepName, 'done'));
			},
			onReviewRequested: (payload: ReviewRequestedPayload) => {
				reconciliationActiveRef.current = false;
				if (pendingAutoApproveRef.current) {
					// User clicked "Create project" while reconciliation was pending.
					// Preserve their manually-entered proposal — skip overwriting with
					// Nova's re-extracted version. Fire the actual approval immediately.
					pendingAutoApproveRef.current = false;
					setSowScreen('review');
					setTimeout(() => handleApproveRef.current(), 0);
					return;
				}
				const novaFields = payload.nova_filled_fields?.length
					? payload.nova_filled_fields
					: deriveNovaFilledFields(payload.proposal);
				// Lock after first extraction — reconciliation REVIEW_REQUESTED events include
				// fields the user resolved via chatbot, which should not count as Nova-filled.
				setNovaFilledFields(prev => (prev.length > 0 ? prev : novaFields));
				setSowFilename(payload.sow_filename ?? null);
				const prev = proposalRef.current;
				const merged = prev
					? mergeProposalEdits(
							payload.proposal,
							prev,
							userEditedFieldsRef.current,
							viewerPersonIdRef.current,
							viewerFullNameRef.current
					  )
					: {
							...payload.proposal,
							owner_person_id: viewerPersonIdRef.current ?? null,
							owner_name: viewerFullNameRef.current ?? null,
					  };
				proposalRef.current = merged;
				setProposal(merged);
				if (!createPlaceholdersEditedRef.current) {
					setCreatePlaceholdersEnabled(payload.create_placeholders_enabled ?? false);
				}
				// Pre-populate placeholder section with "pending" status so the user can
				// see which roles will become placeholders before the commit starts.
				if (payload.placeholder_role_names?.length) {
					setPlaceholderProgresses(payload.placeholder_role_names.map(name => ({name, status: 'pending' as const})));
				}
				if (payload.required_fields_status) {
					setRequiredFieldsStatus(payload.required_fields_status);
				}
				setSowScreen('review');
			},
			onReconciliationQuestion: (q: ReconciliationQuestion) => {
				reconciliationActiveRef.current = true;

				// If this is a date question and the form already has the value,
				// queue a silent answer to be submitted once the current stream ends
				// (onFinished). Calling resumeAgent here, mid-stream, would abort the
				// active fetch and leave an unhandled rejection.
				if (q.question_type === 'date') {
					const isStart = q.field_ref.endsWith('start_date');
					const isEnd = q.field_ref.endsWith('end_date');
					const filledValue = isStart
						? proposalRef.current?.start_date
						: isEnd
						? proposalRef.current?.end_date
						: null;
					if (filledValue) {
						pendingReconciliationAnswerRef.current = filledValue;
						return;
					}
				}

				// Auto-skip if the user already filled this field manually in the form.
				// The backend determines questions at extraction time and doesn't know about
				// manual edits — suppress the question and queue a silent 'Skip' answer.
				const p = proposalRef.current;
				const alreadyFilled = isReconciliationQuestionAlreadyFilled(q, p);
				if (alreadyFilled) {
					// LangGraph sends one question per interrupt, so this should never overwrite
					// a pending answer — but guard anyway to surface it during development.
					if (process.env.NODE_ENV === 'development' && pendingAutoAnswerRef.current !== null) {
						console.warn('[Nova] pendingAutoAnswerRef overwritten — multiple skippable questions in one stream');
					}
					pendingAutoAnswerRef.current = 'Skip';
					setSowScreen('review');
					return;
				}

				// Snapshot nova-filled count from the extraction proposal before user edits start.
				// proposalRef.current holds Nova's extracted state at this point — the form is
				// about to show for the first time, and we must lock before any user interaction.
				setNovaFilledFields(prev => {
					if (prev.length > 0) return prev;
					const p2 = proposalRef.current;
					return p2 ? deriveNovaFilledFields(p2) : [];
				});
				// Pre-populate placeholders from extracted roles so the section is visible
				// as soon as the form appears, before REVIEW_REQUESTED fires.
				setPlaceholderProgresses(prev => {
					if (prev.length > 0) return prev;
					const roles = unfilledRolesRef.current;
					return roles.length ? roles.map(name => ({name, status: 'pending' as const})) : prev;
				});
				addMessage({
					id: SimpleUUID(),
					role: 'assistant',
					content: q.question,
					questionOptions: q.options,
					questionId: q.id,
				});
				// Switch to review so the chat panel is visible for the user to answer
				setSowScreen('review');
			},
		};
	}, [addMessage, updateMessage, closeModal, history, updateStepStatus, setCreatePlaceholdersEnabled]);

	const handleSend = useCallback(
		async (text: string, attachment: AttachedFile | null) => {
			if (!resolvedApiKey) {
				addMessage({
					id: SimpleUUID(),
					role: 'assistant',
					content: 'Setting up your AI assistant… please try again in a moment.',
				});
				return;
			}
			abortRef.current?.abort();
			const controller = new AbortController();
			abortRef.current = controller;

			setPhase('running');
			setWarnings([]);

			// Route to resumeAgent for both approval refinements AND reconciliation answers.
			// Reconciliation: sowScreen='review' + phase='idle' (proposal not yet built).
			// Approval refinement: phase='awaiting_approval'.
			const isGraphInterrupted = phase === 'awaiting_approval' || (isSowMode && sowScreen === 'review');
			if (isGraphInterrupted) {
				addMessage({
					id: SimpleUUID(),
					role: 'user',
					content: attachment ? `📄 ${attachment.filename}${text ? '\n' + text : ''}` : text,
				});
				// Reconciliation answers use state.answer; approval refinements use state.refinement.
				// reconciliationActiveRef is set when RECONCILIATION_QUESTION fires and cleared
				// when REVIEW_REQUESTED fires. phase==='idle' is a secondary fallback for the case
				// where no preliminary proposal was emitted.
				const isReconciliation = reconciliationActiveRef.current || phase === 'idle';
				try {
					await resumeAgent(
						threadIdRef.current,
						false,
						resolvedApiKey,
						buildCallbacks(),
						controller.signal,
						isReconciliation ? undefined : text, // refinement
						personId,
						undefined,
						companyId,
						companyName,
						isReconciliation ? text : undefined // reconciliationAnswer
					);
				} catch (err: unknown) {
					if (err instanceof DOMException && err.name === 'AbortError') {
						setPhase(isReconciliation ? 'idle' : 'awaiting_approval');
						return;
					}
					addMessage({
						id: SimpleUUID(),
						role: 'assistant',
						content: `Error: ${err instanceof Error ? err.message : 'Connection failed'}`,
					});
					setPhase('error');
				}
				return;
			}

			// Initial send — reuse the existing thread if one is active so the agent retains
			// conversation context. Only create a new thread on a genuine fresh start.
			toolCallIdToMsgId.current = {};
			toolNameToMsgId.current = {};
			activeToolCalls.current = {};
			createdProjectIdRef.current = null;
			refiningChipIdRef.current = null;
			setToolStatuses({});
			if (!threadIdRef.current) {
				threadIdRef.current = SimpleUUID();
			}
			const threadId = threadIdRef.current;
			setProposal(null);
			const newUserMsg: ChatMessage = {
				id: SimpleUUID(),
				role: 'user',
				content: attachment ? `📄 ${attachment.filename}${text ? '\n' + text : ''}` : text,
			};
			setMessages(prev =>
				prev.length === 0
					? [newUserMsg]
					: [...prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)), newUserMsg]
			);

			try {
				await invokeAgent(
					text || `Please extract the project brief from the attached document: ${attachment?.filename}`,
					threadId,
					resolvedApiKey,
					buildCallbacks(),
					controller.signal,
					attachment ?? undefined,
					personId,
					companyId,
					companyName
				);
			} catch (err: unknown) {
				if (err instanceof DOMException && err.name === 'AbortError') {
					setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
					setPhase(proposalRef.current ? 'awaiting_approval' : 'idle');
					return;
				}
				addMessage({
					id: SimpleUUID(),
					role: 'assistant',
					content: `Error: ${err instanceof Error ? err.message : 'Connection failed'}`,
				});
				setPhase('error');
			}
		},
		[phase, sowScreen, resolvedApiKey, personId, companyId, companyName, addMessage, updateMessage, buildCallbacks]
	);

	const handleApprove = useCallback(async () => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;

		// Early-path: SOW mode, reconciliation still pending, user manually filled form.
		// Skip outstanding questions, then auto-approve once backend reaches await_approval.
		if (isSowMode && phase === 'idle' && proposal?.name) {
			pendingAutoApproveRef.current = true;
			setPhase('running');
			try {
				await resumeAgent(
					threadIdRef.current,
					false,
					resolvedApiKey,
					buildCallbacks(),
					controller.signal,
					undefined,
					personId,
					undefined,
					companyId,
					companyName,
					'Skip' // reconciliationAnswer — skip the pending question
				);
			} catch (err: unknown) {
				pendingAutoApproveRef.current = false;
				if (err instanceof DOMException && err.name === 'AbortError') {
					setPhase('idle');
					return;
				}
				addMessage({
					id: SimpleUUID(),
					role: 'assistant',
					content: `Error: ${err instanceof Error ? err.message : 'Connection failed'}`,
				});
				setPhase('error');
			}
			return;
		}

		setPhase('committing');
		try {
			await resumeAgent(
				threadIdRef.current,
				true,
				resolvedApiKey,
				buildCallbacks(),
				controller.signal,
				undefined,
				personId,
				proposalRef.current?.budget_type !== 'FIXED_PRICE'
					? ({...proposalRef.current, budget: null} as typeof proposalRef.current)
					: proposalRef.current,
				companyId,
				companyName,
				undefined, // reconciliationAnswer
				createPlaceholdersEnabled
			);
		} catch (err: unknown) {
			if (err instanceof DOMException && err.name === 'AbortError') {
				setPhase('awaiting_approval');
				return;
			}
			// Fall through — check if the project was still committed before the error
		}

		// onFinished already handled the success case — only handle failure here
		if (createdProjectIdRef.current) return;

		setMessages(prev => prev.map(m => (m.isStreaming ? {...m, isStreaming: false} : m)));
		addMessage({id: SimpleUUID(), role: 'assistant', content: 'Project creation failed. Click Cancel to close.'});
		setPhase('error');
	}, [
		isSowMode,
		phase,
		proposal,
		resolvedApiKey,
		personId,
		companyId,
		companyName,
		addMessage,
		buildCallbacks,
		createPlaceholdersEnabled,
	]);

	// Keep handleApproveRef in sync — breaks the circular dep between buildCallbacks and handleApprove
	useEffect(() => {
		handleApproveRef.current = handleApprove;
	}, [handleApprove]);

	// Keep autoAnswerDateQuestionRef in sync. Called by onFinished (never mid-stream)
	// so the previous stream has already ended — no abort needed before resuming.
	useEffect(() => {
		autoAnswerDateQuestionRef.current = (answer: string) => {
			const controller = new AbortController();
			abortRef.current = controller;
			setPhase('running');
			resumeAgent(
				threadIdRef.current,
				false,
				resolvedApiKey,
				buildCallbacks(),
				controller.signal,
				undefined,
				personId,
				undefined,
				companyId,
				companyName,
				answer
			).catch((err: unknown) => {
				if (err instanceof DOMException && err.name === 'AbortError') return;
				if (!isUnmountedRef.current) {
					addMessage({
						id: SimpleUUID(),
						role: 'assistant',
						content: `Error: ${err instanceof Error ? err.message : 'Something went wrong. Please try again.'}`,
					});
					setPhase('error');
				}
			});
		};
	}, [resolvedApiKey, personId, companyId, companyName, buildCallbacks]);

	const handleReset = useCallback(() => {
		abortRef.current?.abort();
		abortRef.current = null;
		clearSession();
		setPhase('idle');
		setMessages([]);
		setProposal(null);
		userEditedFieldsRef.current.clear();
		setWarnings([]);
		toolCallIdToMsgId.current = {};
		toolNameToMsgId.current = {};
		activeToolCalls.current = {};
		createdProjectIdRef.current = null;
		createdProjectRelayIdRef.current = '';
		createdDeliverableCountRef.current = 0;
		createdRoleCountRef.current = 0;
		refiningChipIdRef.current = null;
		setToolStatuses({});
		threadIdRef.current = '';
		setSowScreen('extracting');
		setExtractionSteps(SOW_CHECKLIST_STEPS.map(label => ({label, status: 'pending' as const})));
		setNovaFilledFields([]);
		setSowFilename(null);
		setCreatePlaceholdersEnabled(false);
		createPlaceholdersEditedRef.current = false;
		setPlaceholderProgresses([]);
		unfilledRolesRef.current = [];
		setIsClassificationBlocked(false);
		setClassificationBlockReason(undefined);
		hasAutoStarted.current = false;
		pendingAutoApproveRef.current = false;
		pendingAutoAnswerRef.current = null;
		hadErrorInStreamRef.current = false;
		// activeFile is reset by the onReplace caller, not here, to avoid a flash of the old file
	}, []);

	useEffect(() => {
		if (initialFile && resolvedApiKey && !hasAutoStarted.current) {
			hasAutoStarted.current = true;
			handleSend('', initialFile);
		}
		// hasAutoStarted.current guards against re-firing when handleSend updates on phase changes
	}, [initialFile, resolvedApiKey, handleSend]);

	// Fire pending auto-answer after a reconciliation interrupt stream ends (phase → idle).
	// The stream ends with RUN_FINISHED before the user can answer, so calling handleSend
	// here (after idle) avoids re-entering an in-progress SSE stream.
	useEffect(() => {
		if (pendingAutoAnswerRef.current === null || phase !== 'idle' || sowScreen !== 'review') return;
		const answer = pendingAutoAnswerRef.current;
		pendingAutoAnswerRef.current = null;
		handleSend(answer, null);
	}, [phase, sowScreen, handleSend]);

	const isRunningOrCommitting = phase === 'running' || phase === 'committing';

	// Required fields for SOW project creation — derived from current proposal state
	// so the button re-enables as the user fills in fields, regardless of what the
	// backend reported in required_fields_status.
	const sowMissingFields: string[] =
		isSowMode && proposal
			? [...(!proposal.name ? ['project name'] : []), ...(proposal.client_id == null ? ['client'] : [])]
			: [];
	const handleViewSource = useCallback(() => {
		if (!activeFile) return;
		try {
			const byteString = atob(activeFile.dataBase64);
			const bytes = new Uint8Array(byteString.length);
			for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
			const blob = new Blob([bytes], {type: activeFile.mimeType || 'application/octet-stream'});
			const url = URL.createObjectURL(blob);
			window.open(url, '_blank');
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		} catch {
			// non-critical — view source failure should not interrupt the flow
		}
	}, [activeFile]);

	const cancelBtn = (
		<Modal.TertiaryFooterButton disabled={isRunningOrCommitting} onPress={closeModal}>
			Cancel
		</Modal.TertiaryFooterButton>
	);

	return (
		<Modal onCloseModal={closeModal} size={Modal.SIZE.EXTRA_LARGE} setHeight={true} closeOnInteractOutside={false}>
			<Modal.Title>
				<FlexRow gap="xs" alignItems="center">
					{!isSowMode && (
						<TitleIconBadge>
							<Icon icon="sparkle" size="m" color="white" />
						</TitleIconBadge>
					)}
					{isSowMode ? formatMessage({id: 'sow_review.modal_title'}) : 'AI Project Planning'}
					{isSowMode && sowScreen === 'review' && <SowTitleBadge>From SoW</SowTitleBadge>}
				</FlexRow>
			</Modal.Title>
			<PanelContent>
				{isSowMode && sowScreen === 'extracting' ? (
					activeFile && (
						<SowExtractionProgress
							file={activeFile}
							steps={extractionSteps}
							onReplace={file => {
								setActiveFile(file);
								handleReset();
								// Prevent the auto-start useEffect from re-running with initialFile
								// after handleReset sets hasAutoStarted.current = false.
								hasAutoStarted.current = true;
								setTimeout(() => handleSend('', file), 0);
							}}
							rejectionMessage={
								isClassificationBlocked
									? classificationBlockReason ?? formatMessage({id: 'sow_upload.rejected_default'})
									: null
							}
						/>
					)
				) : (
					<Body>
						{isSowMode ? (
							<SowReviewFormPanel
								proposal={proposal ?? EMPTY_PROPOSAL}
								novaFilledFields={novaFilledFields}
								sowFilename={sowFilename}
								missingRequiredFields={sowMissingFields}
								createPlaceholdersEnabled={createPlaceholdersEnabled}
								onCreatePlaceholdersChange={enabled => {
									createPlaceholdersEditedRef.current = true;
									setCreatePlaceholdersEnabled(enabled);
								}}
								onProposalChange={setProposal}
								onFieldUserEdited={field => {
									userEditedFieldsRef.current.add(field);
								}}
								onViewSource={activeFile ? handleViewSource : undefined}
								clients={clients}
								persons={persons}
								placeholderProgresses={placeholderProgresses.length > 0 ? placeholderProgresses : undefined}
								isBlocked={isClassificationBlocked}
							/>
						) : (
							<ProposalPanel proposal={proposal} warnings={warnings} onProposalChange={setProposal} />
						)}
						<Divider />
						<ChatPanel
							messages={messages}
							phase={phase}
							onSend={handleSend}
							onStop={() => abortRef.current?.abort()}
							toolStatuses={toolStatuses}
							side={isSowMode ? 'right' : 'left'}
						/>
					</Body>
				)}
			</PanelContent>
			{/* Modal.Footer uses React.Children.map — buttons must be direct children, not wrapped in fragments */}
			{isSowMode && sowScreen === 'extracting' ? (
				<Modal.Footer>
					{cancelBtn}
					{isClassificationBlocked && onSwitchToManualCreate && (
						<Modal.PrimaryFooterButton disabled={isRunningOrCommitting} onPress={onSwitchToManualCreate}>
							{formatMessage({id: 'sow_upload.manual_title'})}
						</Modal.PrimaryFooterButton>
					)}
				</Modal.Footer>
			) : (
				<Modal.Footer>
					{cancelBtn}
					{!isSowMode && <Modal.SecondaryFooterButton onPress={handleReset}>Replan</Modal.SecondaryFooterButton>}
					<Modal.PrimaryFooterButton
						disabled={
							isRunningOrCommitting ||
							(isSowMode
								? phase === 'error' || phase === 'idle' || sowMissingFields.length > 0
								: phase !== 'awaiting_approval')
						}
						onPress={handleApprove}
					>
						{isSowMode ? formatMessage({id: 'sow_review.create_project_button'}) : 'Save Project ✓'}
					</Modal.PrimaryFooterButton>
				</Modal.Footer>
			)}
		</Modal>
	);
};

export default AiProjectPlanningModal;
